// 発注推奨に関するロジックをまとめる

import { LEADTIME_MONTH_UNIT, LEADTIME_WEEK_UNIT } from '@/coreDomainsModule/constants';
import { simpleCopy } from '@/utils/deepCopy';
import moment from 'moment';
import { getCalendar } from '@/appLogic/checkCalendar';
import { nullOrUndefined } from '@/coreDomainsModule/extends/number';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { IExtraSimulate } from '@/recommendOrderModule/pages/interfaces/extraSimulation';
import { IBizDays, ICalendar, IDate, IYearMonth } from '@/coreDomainsModule/date';
import { StdInventoryIndex } from '@/coreDomainsModule/entities/inventory';
import { IDailySimResult, SimulationPeriodType } from '@/coreDomainsModule/simulation';
import { IAnnounce, IAnnounceDaily } from '@/coreDomainsModule/entities/forecast';
import { ForecastService } from '@/coreDomainsModule/services/forecastService';
import { IArrival } from '@/coreDomainsModule/entities/arrival';
import { IBusiness } from '@/coreDomainsModule/entities/businessBase';

const MAX_SIM_DAYS = 365;
const SIM_DAYS = 50;

/**
 *
 * 週、ヶ月とうの単位をleadtimeに反映する関数
 * @param leadTime
 * @param spanText
 * @returns '
 */
export const getLeadTimeForSpanText = (leadTime: number, spanText: string): number => {
  let output = -1;
  if (spanText === '週' || spanText === 'week') {
    output = LEADTIME_WEEK_UNIT * leadTime;
  }
  if (spanText === '日' || spanText === 'day') {
    output = leadTime;
  }
  if (spanText === 'ヶ月' || spanText === 'month') {
    output = LEADTIME_MONTH_UNIT * leadTime;
  }
  return output;
};

/**
 * 年と月を指定して、calendarオブジェクトからその月の稼働日リストを取り出す
 * @param calendar
 * @param year
 * @param month
 */
export const getWorkDays = (calendar: ICalendar, year: number, month: number): number[] | undefined => {
  if (calendar === undefined || null) {
    return undefined;
  }
  const workdays = calendar.workDays as IBizDays[];
  const yearMonthInfo: IYearMonth = {
    year: year,
    month: month,
  };
  for (let i = 0; i < workdays.length; i++) {
    if (DateService.equalYearMonthInfo(workdays[i], yearMonthInfo)) {
      return workdays[i].days;
    }
  }
  return undefined;
};

type DailyAnnounceContext = {
  dailyAmounts: { [key: number]: number };
  fallbackAmount: number;
  fallbackStartDay: number;
  workDays: number[];
};

/**
 * 日別内示の月別コンテキストを作成する
 *
 * 日別内示がある場合:
 * - 営業日カレンダーを無視して、日付の存在のみで判定する
 * - フォールバック条件: その月内で自身より未来の日別内示がないこと
 * - フォールバック計算: (月次内示 - 日次内示の総和) / 残りの稼働日数
 * @param calendar - 稼働日カレンダー
 * @param announces - 客先内示（月次）
 * @param announceDaily - 客先内示（日別）
 */
const buildDailyAnnounceContexts = (
  calendar: ICalendar,
  announces: IAnnounce[],
  announceDaily: IAnnounceDaily[]
): { [key: string]: DailyAnnounceContext } => {
  const contexts: { [key: string]: DailyAnnounceContext } = {};
  const monthlyAnnounceMap: { [key: string]: number } = {};
  announces.forEach((announce) => {
    monthlyAnnounceMap[`${announce.year}-${announce.month}`] = announce.amount;
  });
  const monthKeys = new Set<string>(Object.keys(monthlyAnnounceMap));
  announceDaily.forEach((daily) => {
    monthKeys.add(`${daily.year}-${daily.month}`);
  });

  monthKeys.forEach((monthKey) => {
    const [yearText, monthText] = monthKey.split('-');
    const year = Number(yearText);
    const month = Number(monthText);
    const workDays = getWorkDays(calendar, year, month) || [];
    const monthlyAmount = monthlyAnnounceMap[monthKey] ?? 0;
    const dailyCandidates = announceDaily
      .filter((daily) => daily.year === year && daily.month === month)
      .sort((a, b) => {
        const aKey = (a.manageInitiatedYearMonth?.year ?? 0) * 100 + (a.manageInitiatedYearMonth?.month ?? 0);
        const bKey = (b.manageInitiatedYearMonth?.year ?? 0) * 100 + (b.manageInitiatedYearMonth?.month ?? 0);
        if (aKey !== bKey) return bKey - aKey;
        return (a.day ?? 0) - (b.day ?? 0);
      });

    const dailyAmountByDay: { [key: number]: number } = {};
    dailyCandidates.forEach((daily) => {
      if (dailyAmountByDay[daily.day] === undefined) {
        dailyAmountByDay[daily.day] = daily.amount;
      }
    });

    const hasDailyAmounts = Object.keys(dailyAmountByDay).length > 0;

    if (hasDailyAmounts) {
      // 日別内示がある場合: 営業日カレンダーを無視して日付順で処理
      const maxDailyDay = Math.max(...Object.keys(dailyAmountByDay).map(Number));
      const dailySum = Object.values(dailyAmountByDay).reduce((sum, amount) => sum + amount, 0);
      const remainingAmount = monthlyAmount - dailySum;

      // 残りの稼働日数を計算（maxDailyDayより後の稼働日）
      const remainingWorkDays = workDays.filter((day) => day > maxDailyDay).length;

      const fallbackAmount = remainingWorkDays > 0 && remainingAmount > 0 ? remainingAmount / remainingWorkDays : 0;

      // フォールバック開始日（日別内示の最終日の翌日から）
      const fallbackStartDay = maxDailyDay + 1;

      contexts[monthKey] = {
        dailyAmounts: dailyAmountByDay,
        fallbackAmount,
        fallbackStartDay,
        workDays,
      };
    } else {
      // 日別内示がない場合: 従来通り月次内示から均等配分
      const fallbackAmount = workDays.length > 0 ? monthlyAmount / workDays.length : 0;

      contexts[monthKey] = {
        dailyAmounts: {},
        fallbackAmount,
        fallbackStartDay: 1,
        workDays,
      };
    }
  });
  return contexts;
};

/**
 * 対象日の内示量を取得する
 *
 * 日別内示がある場合:
 * - 対象日に日別内示があればその値を返す
 * - 対象日がフォールバック開始日以降かつ稼働日であればフォールバック値を返す
 * - それ以外は0を返す
 * @param contexts - 日別内示コンテキスト
 * @param announces - 月次内示
 * @param calendar - 稼働日カレンダー
 * @param targetDate - 対象日
 */
const getDailyAnnounceAmount = (
  contexts: { [key: string]: DailyAnnounceContext },
  announces: IAnnounce[],
  calendar: ICalendar,
  targetDate: IDate
): number => {
  const monthKey = `${targetDate.year}-${targetDate.month}`;
  const context = contexts[monthKey];
  const workDays = context?.workDays ?? getWorkDays(calendar, targetDate.year, targetDate.month) ?? [];
  if (!workDays.includes(targetDate.day)) {
    return 0;
  }
  if (context) {
    if (context.dailyAmounts[targetDate.day] !== undefined) {
      return context.dailyAmounts[targetDate.day];
    }
    // フォールバック開始日以降かつ稼働日の場合のみフォールバック値を返す
    if (targetDate.day >= context.fallbackStartDay) {
      return context.fallbackAmount;
    }
    return 0;
  }
  const monthlyAmount = ForecastService.getAnnounceValue(announces, {
    year: targetDate.year,
    month: targetDate.month,
  });
  if (workDays.length === 0) {
    return 0;
  }
  return monthlyAmount / workDays.length;
};

/**
 * 指定日から一定日数分の内示量を合算する
 * @param contexts - 日別内示コンテキスト
 * @param announces - 月次内示
 * @param calendar - 稼働日カレンダー
 * @param startDate - 開始日
 * @param spanDays - 合算日数
 */
const sumDailyAnnounceBySpan = (
  contexts: { [key: string]: DailyAnnounceContext },
  announces: IAnnounce[],
  calendar: ICalendar,
  startDate: IDate,
  spanDays: number
): number => {
  if (spanDays <= 0) {
    return 0;
  }
  let total = 0;
  const dateCursor = new Date(startDate.year, startDate.month - 1, startDate.day);
  for (let i = 0; i < spanDays; i++) {
    total += getDailyAnnounceAmount(contexts, announces, calendar, {
      year: dateCursor.getFullYear(),
      month: dateCursor.getMonth() + 1,
      day: dateCursor.getDate(),
    });
    dateCursor.setDate(dateCursor.getDate() + 1);
  }
  return total;
};

/**
 * 発注推奨量を正規化する
 * MOQあり(数字)＆容量あり(数字) -> MOQ以上かつ容量ごとの発注推奨数量を表示
 * MOQあり(数字)＆容量なし(文字列) -> MOQ以上かつ容量を"1"として発注推奨数量を表示
 * MOQなし(文字列)＆容量あり(数字) -> 容量ごとの発注推奨数量を表示
 * MOQなし(文字列)＆容量なし(文字列) -> 容量を"1"として発注推奨数量を表示
 * @param diffAmount 不足分の在庫(0<=)
 * @param packageAmount パッケージ単位(0<)
 * @param moq 最小発注単位(0<)
 * @returns '
 */
export const calculationOrderAmount = (diffAmount: number, packageAmount?: number, moq?: number): number => {
  let orderAmount = 0;

  // diffAmountがマイナス値の時は、想定外なので、-1を返す
  if (diffAmount < 0) {
    console.error('diffAmount is negative');
    return -1;
  }

  if (!packageAmount) {
    packageAmount = -1;
  }
  if (!moq) {
    moq = -1;
  }

  // diffAmountが0の時は、0を返す
  if (diffAmount === 0) {
    return 0;
  }

  if (diffAmount >= moq) {
    if (packageAmount > 0) {
      orderAmount = Math.ceil(diffAmount / packageAmount) * packageAmount;
    } else {
      orderAmount = diffAmount;
    }
  } else {
    if (packageAmount > 0) {
      orderAmount = Math.ceil(moq / packageAmount) * packageAmount;
    } else {
      orderAmount = moq;
    }
  }
  return orderAmount;
};

/**
 * 次の月を取得する
 * @param year - 年
 * @param month - 月
 * @returns [次の年, 次の月]
 */
const getNextMonth = (year: number, month: number): [number, number] => {
  if (month === 12) {
    return [year + 1, 1];
  }
  return [year, month + 1];
};

/**
 * 標準在庫指数の計算（月次内示ベース・加重平均）
 * @param stdInventoryIndices - 標準在庫指数
 * @param simData - 該当日の情報
 * @param calender - 営業日カレンダー
 * @param announce - 月次内示
 * @returns [minNum, maxNum]
 */
const calculationStdInventoryIndicesAmountByMonthly = (
  stdInventoryIndices: StdInventoryIndex,
  simData: IDailySimResult,
  calender: ICalendar,
  announce: IAnnounce[]
): [number, number] => {
  const simulateDayCount = 30;
  let minNum = 0;
  let maxNum = 0;

  // 2ヶ月先までのカレンダーを取得
  const [nextMonthYear, nextMonthMonth] = getNextMonth(simData.year, simData.month);
  const nextMonthCalendar = getWorkDays(calender, nextMonthYear, nextMonthMonth) || [];
  const [twoMonthLaterYear, twoMonthLaterMonth] = getNextMonth(nextMonthYear, nextMonthMonth);
  const twoMonthLaterCalendar = getWorkDays(calender, twoMonthLaterYear, twoMonthLaterMonth) || [];

  // 1ヶ月先までの客先内示を取得
  const nowMonthAnnounce = announce.find((a) => a.year === simData.year && a.month === simData.month);
  const nowMonthAnnounceValue = nowMonthAnnounce?.amount ?? 0;

  const nextMonthAnnounce = announce.find((a) => a.year === nextMonthYear && a.month === nextMonthMonth);
  const nextMonthAnnounceValue = nextMonthAnnounce?.amount ?? 0;

  const { min, max, option } = stdInventoryIndices;

  if (nextMonthCalendar.length === 0) {
    // 当月の範囲内で消費される在庫を計算する
    minNum =
      ((nowMonthAnnounceValue / simData.allDayCounts) *
        (simData.allDayCounts - simData.bizdayInMonth + 1) *
        getLeadTimeForSpanText(min, option.unit)) /
      simData.allDayCounts;
    maxNum =
      ((nowMonthAnnounceValue / simData.allDayCounts) *
        (simData.allDayCounts - simData.bizdayInMonth + 1) *
        getLeadTimeForSpanText(max, option.unit)) /
      simData.allDayCounts;
    return [Math.max(minNum, 0), Math.max(maxNum, 0)];
  }

  if (simData.bizdayInMonth < nextMonthCalendar.length || twoMonthLaterCalendar.length === 0) {
    // 当月、翌月の範囲内で消費される在庫を計算する
    minNum =
      ((nowMonthAnnounceValue / simData.allDayCounts) * (simData.allDayCounts - simData.bizdayInMonth + 1) +
        (nextMonthAnnounceValue / nextMonthCalendar.length) * (simData.bizdayInMonth - 1)) *
      (getLeadTimeForSpanText(min, option.unit) / simulateDayCount);
    maxNum =
      ((nowMonthAnnounceValue / simData.allDayCounts) * (simData.allDayCounts - simData.bizdayInMonth + 1) +
        (nextMonthAnnounceValue / nextMonthCalendar.length) * (simData.bizdayInMonth - 1)) *
      (getLeadTimeForSpanText(max, option.unit) / simulateDayCount);
  } else {
    // 当月、翌月、翌々月の範囲内で消費される在庫を計算する
    const twoMonthLaterAnnounce = announce.find((a) => a.year === twoMonthLaterYear && a.month === twoMonthLaterMonth);
    const twoMonthLaterAnnounceValue = twoMonthLaterAnnounce?.amount ?? 0;

    minNum =
      ((nowMonthAnnounceValue / simData.allDayCounts) * (simData.allDayCounts - simData.bizdayInMonth + 1) +
        nextMonthAnnounceValue +
        (twoMonthLaterAnnounceValue / twoMonthLaterCalendar.length) * (twoMonthLaterCalendar.length - simData.bizdayInMonth - 1)) *
      (getLeadTimeForSpanText(min, option.unit) / simulateDayCount);

    maxNum =
      ((nowMonthAnnounceValue / simData.allDayCounts) * (simData.allDayCounts - simData.bizdayInMonth + 1) +
        nextMonthAnnounceValue +
        (twoMonthLaterAnnounceValue / twoMonthLaterCalendar.length) * (twoMonthLaterCalendar.length - simData.bizdayInMonth - 1)) *
      (getLeadTimeForSpanText(max, option.unit) / simulateDayCount);
  }

  return [Math.max(minNum, 0), Math.max(maxNum, 0)];
};

/**
 * 標準在庫指数　→ [minNum, maxNum]　へ変換する(新)
 * @param stdInventoryIndices
 * @param simData 該当日の情報
 * @param calender 営業日カレンダー
 * @param announce　内示 {"0": 0,"1": 0,...   ,"11": 0}
 * @param announceDaily 日別内示
 * @param dailyAnnounceContexts 日別内示コンテキスト
 * @returns '
 */
const calculationStdInventoryIndicesAmount = (
  stdInventoryIndices: StdInventoryIndex,
  simData: IDailySimResult,
  calender: ICalendar,
  announce: IAnnounce[],
  announceDaily: IAnnounceDaily[] = [],
  dailyAnnounceContexts?: { [key: string]: DailyAnnounceContext }
): [number, number] => {
  // stdInventoryIndicesが{}の場合は、[0,0]を返す
  if (!stdInventoryIndices || Object.keys(stdInventoryIndices).length === 0) {
    return [0, 0];
  }

  const contexts = dailyAnnounceContexts ?? buildDailyAnnounceContexts(calender, announce, announceDaily);
  const monthKey = `${simData.year}-${simData.month}`;
  const hasDailyContext = monthKey in contexts;

  if (hasDailyContext) {
    // 日次内示がある月: 日次内示+均等割ベース
    const { min, max, option } = stdInventoryIndices;
    const minSpanDays = getLeadTimeForSpanText(min, option.unit);
    const maxSpanDays = getLeadTimeForSpanText(max, option.unit);
    // 翌日の日次内示から計算を行う
    const startDate: IDate = DateService.getShiftedIYearMonthByDays(simData.year, simData.month, simData.day, 1);
    const requiredMin = sumDailyAnnounceBySpan(contexts, announce, calender, startDate, minSpanDays);
    const requiredMax = sumDailyAnnounceBySpan(contexts, announce, calender, startDate, maxSpanDays);
    return [requiredMin, requiredMax];
  } else {
    // 日次内示がない月: 加重平均ベース
    return calculationStdInventoryIndicesAmountByMonthly(stdInventoryIndices, simData, calender, announce);
  }
};

/**
 * シミュレーション結果1日分を格納するオブジェクトを生成する
 * @param date
 * @param bizday
 */

const getInitializedForecast = (date: Date, bizday?: number, dayCount?: number): IDailySimResult => {
  const daily: IDailySimResult = {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
    bizdayInMonth: typeof bizday === 'number' ? bizday : 0,
    allDayCounts: typeof dayCount === 'number' ? dayCount : 0,
    customerAnnounce: 0,
    inventory: 0,
    arrival: 0,
    willArrival: 0,
    order: 0,
    dailyInventoryLowerBound: 0,
  };
  return daily;
};

/**
 * シミュレーション結果を入れるオブジェクトのArrayを作成する
 * その中には当月の稼働日(bizdayInMonth)もセットする
 * @param calendar
 * @param startDate
 * @param period
 */
const getBaseData = (calendar: ICalendar, startDate: Date, period: number): IDailySimResult[] => {
  const date = startDate;
  const result: IDailySimResult[] = [];
  let thisMonth: number = date.getMonth();
  let workdays = (getWorkDays(calendar, date.getFullYear(), date.getMonth() + 1) || []) as number[];
  let pos = 0;
  let day = 1;

  for (let i = 0; i < period; i++) {
    if (date.getMonth() !== thisMonth) {
      thisMonth = date.getMonth();
      workdays = (getWorkDays(calendar, date.getFullYear(), date.getMonth() + 1) || []) as number[];
      pos = 0;
      day = 1;
    }
    const today = date.getDate();

    // workdaysが空の場合は、稼働日がないので、全て非稼働日として扱う
    if (workdays.length === 0 || workdays.findIndex((day) => day === today) === -1) {
      result.push(getInitializedForecast(date)); // 非稼働日
      date.setDate(date.getDate() + 1);
      continue;
    }

    // 例： workdays = [2,3,4,5,6,9,10,11,,,,]
    //     today = 7  (7日)
    for (let j = pos; j < workdays.length; j++) {
      pos++;
      if (workdays[j] < today) {
        continue;
      }
      if (workdays[j] > today) {
        for (let k = 0; k < workdays[j] - today; k++) {
          result.push(getInitializedForecast(date)); // 非稼働日
          date.setDate(date.getDate() + 1);
        }
      }
      break;
    }
    // ここに来た時点で、workdays配列のposの位置の日付が、作成対象の日付になっている
    result.push(getInitializedForecast(date, day, workdays.length)); // 稼働日
    day++;
    date.setDate(date.getDate() + 1);
  }
  return result;
};

/**
 * undefinedを0に変換する関数
 * @param a
 * @returns '
 */
const avoidUndefined = function (a: number | undefined) {
  return a || 0;
};

const getEvalValue = function (arrival: IArrival | IDate): number {
  if (!arrival) {
    return 0;
  }
  return arrival.year * 10000 + arrival.month * 100 + arrival.day;
};

const calculateDays = function (time: number) {
  // unixtimeを日に変換する
  const dayUnit = 86400000;
  return time / dayUnit;
};

/**
 *　mvp：arrival(not arrivals)に関する全ての処理は無視しています（移行中のため、後で削除予定）
 * @param business - 取引情報
 * @param calendar - 稼働日カレンダー
 * @param startDate - 計算開始日
 * @param setPeriod - 計算したい日数（稼働日ベース）
 * @param extraSimulation - シミュレーション用：extraSimulationを入れると、その情報を元にシミュレーションを行う(recommendの推奨は行わないようになる）
 * @param onlyFirstRecommend - trueの場合、recommendの計算は最初の一回だけ行い以降はスキップ
 * @param recommendStartDate - 指定すると、この日付より前のrecommendを抑制する
 * @returns - 予測結果
 */
const simulateInventory = (
  business: IBusiness,
  calendar: ICalendar,
  startDate: Date,
  setPeriod: number,
  extraSimulation?: IExtraSimulate,
  onlyFirstRecommend = false,
  recommendStartDate?: Date
): {
  simData: IDailySimResult[];
  dailyInventoryLowerBounds: { [key: string]: number[] };
  dailyInventoryHigherBounds: { [key: string]: number[] };
} => {
  // 分納情報を加工するメソッド
  const processPartialDeliveries = (partialDeliveries: IArrival[]): IArrival[] => {
    const processPartialDeliveries: IArrival[] = [];
    partialDeliveries.forEach((partialDelivery) => {
      const arrivalDate = partialDelivery.crossDate;
      // arrivalDateはstartDateより前の日付の場合は無視する(inventoryは過去の発注を反映しているので)
      if (arrivalDate != null && getEvalValue(arrivalDate) > getEvalValue(DateService.convertDateToIDate(startDate))) {
        processPartialDeliveries.push({
          arrivalId: partialDelivery.arrivalId,
          amount: partialDelivery.amount,
          isFixOrdered: true,
          leadTime: business.leadTime as number,
          day: arrivalDate.day,
          month: arrivalDate.month,
          year: arrivalDate.year,
          fixOrderDate: undefined,
          crossDate: undefined,
          status: partialDelivery.status,
          memoText: undefined,
          partialDeliveries: undefined,
          number: undefined,
          updateAt: undefined,
        });
      }
    });
    return processPartialDeliveries;
  };

  startDate = simpleCopy(startDate);
  const period: number = setPeriod;
  if (typeof business.customerAnnounce === 'undefined') {
    throw new Error('no forecast');
  }

  if (typeof business.stdInventoryIndices === 'undefined') {
    throw new Error('no stdInventoryIndices');
  }

  // 発注を入れる
  extraSimulation?.extraRecommend?.forEach((recommendation) => {
    const arrivalDate = recommendation.crossDate;
    // arrivalDateはstartDateより前の日付の場合は無視する(inventoryは過去の発注を反映しているので)
    if (arrivalDate != null && getEvalValue(arrivalDate) > getEvalValue(DateService.convertDateToIDate(startDate))) {
      business.arrivals?.push({
        arrivalId: recommendation.arrivalId,
        amount: recommendation.amount,
        isFixOrdered: true,
        leadTime: business.leadTime as number,
        day: arrivalDate.day,
        month: arrivalDate.month,
        year: arrivalDate.year,
        fixOrderDate: undefined,
        crossDate: undefined,
        status: recommendation.status,
        memoText: undefined,
        partialDeliveries: recommendation.partialDeliveries ? processPartialDeliveries(recommendation.partialDeliveries) : undefined,
        number: undefined,
        updateAt: undefined,
      });
    }
  });

  // extraSimulationがある場合は、extraSimulationの情報を元にシミュレーションを行う
  if (!isExtraSimulation(extraSimulation)) {
    business.recommend = []; // forecast.recommendは昔の状態が残ると困る
    if (extraSimulation) {
      business.leadTime = extraSimulation.extraLeadTime ?? business.leadTime;
      business.stdInventoryIndices['標準在庫指数'] =
        simpleCopy(extraSimulation.extraStdInventory) ?? business.stdInventoryIndices['標準在庫指数'];
      business.customerAnnounce = simpleCopy(extraSimulation.extraAnnounce) ?? business.customerAnnounce;
    }
  } else if (extraSimulation) {
    // extraStartDateがある場合は、recommendを維持したままシミュレーションを行う
    if (extraSimulation.extraStartDate) {
      business.recommend = simpleCopy(extraSimulation.extraRecommend) ?? business.recommend; // extraRecommendを流す
    } else {
      business.recommend = []; // それ以外の場合は、recommendを初期化し、extraRecommendを流す
    }
    business.leadTime = extraSimulation.extraLeadTime ?? business.leadTime;
    business.stdInventoryIndices['標準在庫指数'] =
      simpleCopy(extraSimulation.extraStdInventory) ?? business.stdInventoryIndices['標準在庫指数'];
    business.customerAnnounce = simpleCopy(extraSimulation.extraAnnounce) ?? business.customerAnnounce;
  }

  // business.minusInventory = undefined; // 計算のたびにminusInventoryを初期化する TODO: 今は使ってない？？
  //const simData: IDailySimResult[] = getBaseData(calendar, startDate, period + EXTRA_SIM_DAYS); // 実際には提示しないが後半部分の辻褄を合わせるために期間を延長する
  const simData: IDailySimResult[] = simpleCopy(getBaseData(calendar, startDate, MAX_SIM_DAYS)); // 一旦最大のシミュレーション期間（＝１年）のデータ構造を作る  // TODO: テストのために今までと同じ50日にしているが、365に変更したい

  // 処理1-1：businessの中のforecastのannounceを読み取る
  const announce: IAnnounce[] = simpleCopy(business.customerAnnounce as IAnnounce[]);
  const announceDaily: IAnnounceDaily[] = simpleCopy(business.customerAnnounceDaily ?? []);
  const dailyAnnounceContexts = buildDailyAnnounceContexts(calendar, announce, announceDaily);
  if (announce.length === 0 && announceDaily.length === 0) {
    console.log('announce is empty');
    return { simData, dailyInventoryLowerBounds: {}, dailyInventoryHigherBounds: {} };
  }

  // 処理1-2：businessの中のforecastのarrivalを読み取り、入荷日順に並べる
  // 処理1-2-1: 入荷予定の情報を取り出す
  const useArrivals = simpleCopy(business.arrivals) || [];
  // 処理1-2-2: 入荷予定の情報に分納情報がある場合は、分納情報を取り出して親の入荷予定情報を削除する
  const useArrivals2: IArrival[] = [];
  useArrivals.forEach((arrival) => {
    if (arrival.status === 'PartialDelivery' && arrival.partialDeliveries && arrival.partialDeliveries.length > 0) {
      arrival.partialDeliveries.forEach((partial) => {
        useArrivals2.push(partial);
      });
    } else {
      useArrivals2.push(arrival);
    }
  });
  // 処理1-2-3: 入荷日を確認して、同一日に入荷予定がある場合は、amountを合算する
  // 処理-1-2-3-1: 入荷日をキーにして、amountを合算したmapを作成する
  const arrivalMap: { [key: string]: IArrival } = {};
  useArrivals2.forEach((arrival) => {
    const key = `${arrival.year}-${arrival.month}-${arrival.day}`;
    if (arrivalMap[key]) {
      arrivalMap[key].amount += arrival.amount; // 同一日に入荷予定がある場合は、amountを合算する
    } else {
      arrivalMap[key] = simpleCopy(arrival);
    }
  });

  // 処理-1-2-3-2: mapの情報をuseArrivals3に変換する
  const useArrivals3: IArrival[] = [];
  Object.keys(arrivalMap).forEach((key) => {
    useArrivals3.push(arrivalMap[key]);
  });

  // 処理1-2-3：arrivalを入荷日順に並べる
  const willArrive: IArrival[] =
    useArrivals3
      ?.filter((arrival) => {
        return arrival.isFixOrdered; // 発注から生成された情報のみを取得する（それ以外は「入荷実績」なので）
      })
      .sort((a: IArrival, b: IArrival) => {
        const A = avoidUndefined(a.year) * 10000 + avoidUndefined(a.month) * 100 + avoidUndefined(a.day);
        const B = avoidUndefined(b.year) * 10000 + avoidUndefined(b.month) * 100 + avoidUndefined(b.day);
        return A < B ? -1 : 1;
      }) ?? [];

  // 処理2：条件設定
  // 　A. 過去に発注した情報から入荷情報を反映する(TODO: mvp対応によりここらへんは機能していない)
  // 　B. 内示(announce）を日割り計算してsimDataに埋めていく
  let count = 0;
  // const endDaysCount = MAX_SIM_DAYS;
  for (let i = 0; i < simData.length; i++) {
    // 最大で365日まで実施する。推奨日が見つかった時は、(period＋EXTRA_SIM_DAYS)日間で終了する。period日を過ぎても見つからない時は、見つかった日からEXTRA_SIM_DAYS日後までで終了する。
    // if (simData[i].bizdayInMonth === 0) continue; // 非稼働日 （最初から含まないようになったのでこの処理は不要）

    // A. 過去に発注した情報から入荷情報を反映する (同日到着のエントリが複数あるかもしれない)
    for (let k = count; k < willArrive.length; k++) {
      if (simData[i].year === willArrive[k].year && simData[i].month === willArrive[k].month && simData[i].day >= willArrive[k].day) {
        (simData[i + 1] as any).arrival += willArrive[k].amount;
        (simData[i] as any).willArrival += willArrive[k].amount; // グラフ描画用
        count = k + 1;
      }
    }

    // B. 内示(announce）を日割り計算してsimDataに埋めていく
    if (simData[i].bizdayInMonth === 0) continue; // 非稼働日
    simData[i].customerAnnounce = getDailyAnnounceAmount(dailyAnnounceContexts, announce, calendar, {
      year: simData[i].year,
      month: simData[i].month,
      day: simData[i].day,
    });
  }

  // 処理3: メインの計算
  //   A. 在庫を計算する
  //   B. 入荷基準を計算する
  //   C. 入荷を反映する
  //   D. 入荷によって、在庫が日次安全在庫下限数量を上回った場合、recommendを取り消す。
  //   E. 在庫が基準を下回れば、その日にMOQが入荷するように日を遡って発注情報を入れる（ただし、発注日がシミュレーション期間前になってしまう場合はスキップする）
  const leadTime = business.leadTime as number;
  const moq = business.MOQ as number;
  const dailyInventoryLowerBounds = {}; // simDataが情報を持ちすぎるので
  const dailyInventoryHigherBounds = {}; // simDataが情報を持ちすぎるので
  for (const key of Object.keys(business.stdInventoryIndices || {})) {
    dailyInventoryLowerBounds[key] = new Array(simData.length);
    dailyInventoryHigherBounds[key] = new Array(simData.length);
  }
  for (let i = 0; i < simData.length; i++) {
    // 提示する日数は最初に与えられたパラメータ（period）だけで良い
    // A. 在庫を計算する
    if (i > 0) {
      simData[i].inventory = (simData[i - 1].inventory as number) - (simData[i].customerAnnounce as number);
    } else {
      simData[i].inventory = business.inventory as number; // 初日のみ内示は引かない
    }
    // B. 入荷基準を計算する
    let [requiredMinStock, requiredMaxStock] = [0, 0];

    [requiredMinStock, requiredMaxStock] = calculationStdInventoryIndicesAmount(
      business.stdInventoryIndices['標準在庫指数'],
      simData[i],
      calendar,
      announce,
      announceDaily,
      dailyAnnounceContexts
    );
    // 内示によって必要とされる在庫数を計算する(maxの値を使用)
    // for (let k = 0; k < stdInventoryMaxDate; k++) {
    //   if (!simData[i + k]) {
    //     // ここがundefinedになる可能性がある？breakするのが正しいかわからないが、とりあえず
    //     // errorを回避するためにbreak
    //     break;
    //   }
    //   if (stdInventoryMinDate > k) {
    //     requiredMinStock += simData[i + k].customerAnnounce as number;
    //   }
    //   requiredMaxStock += simData[i + k].customerAnnounce as number;
    // }

    // C. 入荷を反映る
    simData[i].inventory = (simData[i].inventory as number) + (simData[i].arrival as number);

    if (i + 1 === period) break; // period行ったらここで終了

    // A-1. 非稼働日の対応
    if (simData[i].bizdayInMonth === 0 && dailyInventoryLowerBounds['標準在庫指数'] && dailyInventoryHigherBounds['標準在庫指数']) {
      if (i > 0) {
        dailyInventoryLowerBounds['標準在庫指数'][i] = dailyInventoryLowerBounds['標準在庫指数'][i - 1];
        dailyInventoryHigherBounds['標準在庫指数'][i] = dailyInventoryHigherBounds['標準在庫指数'][i - 1];
      } else {
        dailyInventoryLowerBounds['標準在庫指数'][i] = 0; // 初日
        dailyInventoryHigherBounds['標準在庫指数'][i] = 0; // 初日
      }
      continue;
    }
    let [dailyInventoryLowerBoundMin, dailyInventoryLowerBoundMax] = [-1, -1];
    let dailyInventoryLowerBoundTargetName = '';
    for (const [name, value] of Object.entries(business.stdInventoryIndices || {})) {
      // 標準在庫指数 = (requiredStock / simData[i].allDayCounts) * 標準在庫指数(max) = 在庫の当日の減少分 * 標準在庫指数(max) * unit　を日次安全在庫下限数量とする
      // dailyInventoryLowerBounds[name][i] = requiredStock * (value.min / MONTH_UNIT); // 一応残しておく(変更前)
      dailyInventoryLowerBounds[name][i] = requiredMinStock;
      dailyInventoryHigherBounds[name][i] = requiredMaxStock;
      dailyInventoryLowerBoundMin = requiredMinStock; // 標準在庫指数（min)の値を使用する
      if (dailyInventoryLowerBoundMax < 0 || dailyInventoryLowerBoundMax > dailyInventoryLowerBounds[name][i]) {
        dailyInventoryLowerBoundTargetName = name;
        dailyInventoryLowerBoundMax = requiredMaxStock; // 標準在庫指数（max)の値を使用する
      }
      // D. 入荷によって、在庫が日次安全在庫下限数量を上回った場合、recommendを取り消す。
      if (dailyInventoryLowerBounds[name][i] < (simData[i].inventory as number)) {
        // business.recommend = [];
      }
    }
    // E. 在庫が日次安全在庫下限数量(dailyInventoryLowerBound)を下回れば、その日にMOQが入荷するように日を遡って発注のrecommendをセットする
    //（ただし、発注日がシミュレーション期間前になってしまう場合はスキップする）

    if (extraSimulation?.extraStartDate) {
      // extraStartDateがある場合は、extraStartDate以前の日付については、recommendをセットしない
      const extraStartDate = new Date(
        extraSimulation.extraStartDate?.getFullYear(),
        extraSimulation.extraStartDate?.getMonth(),
        extraSimulation.extraStartDate?.getDate()
      );
      const simulationDate = new Date(simData[i].year, simData[i].month - 1, simData[i].day);
      extraStartDate.setHours(0, 0, 0, 0);
      simulationDate.setHours(0, 0, 0, 0);
      if (extraStartDate.getTime() > simulationDate.getTime()) {
        continue;
      }
    }

    // recommendStartDateが指定されている場合、その日付以前はrecommendをスキップ
    if (recommendStartDate) {
      // 最後の発注の1日後から推奨を出す
      const recStart = new Date(recommendStartDate.getFullYear(), recommendStartDate.getMonth(), recommendStartDate.getDate() + 1);
      const simulationDate = new Date(simData[i].year, simData[i].month - 1, simData[i].day);
      recStart.setHours(0, 0, 0, 0);
      simulationDate.setHours(0, 0, 0, 0);
      if (recStart.getTime() > simulationDate.getTime()) {
        continue;
      }
    }

    // extraSimulationがない | extraSimulationはあるが、extraRecommendがない | extraStartDateがある場合
    if (
      dailyInventoryLowerBoundMin > (simData[i].inventory as number) &&
      (!isExtraSimulation(extraSimulation) ||
        (extraSimulation && !extraSimulation.extraRecommend) ||
        (extraSimulation && extraSimulation.extraStartDate))
    ) {
      const setDate = simpleCopy(new Date(simData[i].year, simData[i].month - 1, simData[i].day));
      setDate.setDate(setDate.getDate() - leadTime);
      const recommendation: IArrival = {
        arrivalId: '',
        year: setDate.getFullYear(),
        month: setDate.getMonth() + 1,
        day: setDate.getDate(),
        amount: calculationOrderAmount(
          dailyInventoryLowerBoundMax - (simData[i].inventory as number),
          nullOrUndefined(business.orderPackage ? business.orderPackage : business.package), // 発注単位が存在する場合、発注単位を容量として使用する
          nullOrUndefined(business.MOQ)
        ),
        leadTime: business.leadTime as number,
        crossDate: { year: simData[i].year, month: simData[i].month, day: simData[i].day },
        isFixOrdered: undefined,
        fixOrderDate: undefined,
        status: undefined,
        memoText: undefined,
        partialDeliveries: undefined,
        number: undefined,
        updateAt: undefined,
      };

      // 初日は推奨発注とか出さない
      if (i > 0) {
        business.recommend.push(recommendation);

        // 入荷予定がシミュレーションの範囲を出ない場合、在庫に加算する
        if (simData[i + 1] == null || simData[i + 1].arrival == null) {
          simData[i + 1].arrival = recommendation.amount; // willArrivalの後始末用：arrivalで帳尻を合わせる
          simData[i].willArrival = recommendation.amount; // グラフ描画用
        } else {
          (simData[i + 1] as any).arrival += recommendation.amount; // willArrivalの後始末用：arrivalで帳尻を合わせる
          (simData[i] as any).willArrival += recommendation.amount; // グラフ描画用
        }
      }
    }
    if ((simData[i].inventory as number) < 0) {
      // business.minusInventory = {
      //   date: { year: simData[i].year, month: simData[i].month, day: simData[i].day },
      //   value: simData[i].inventory as number,
      // };
    }
    // 最初の推奨発注を取得したいだけの場合は、推奨発注情報を一つでも取得できていたらループを抜ける
    if (onlyFirstRecommend && business.recommend && business.recommend.length > 0) {
      break;
    }
  }
  // period以下になるように、splice
  simData.splice(period);
  for (const name of Object.keys(dailyInventoryLowerBounds)) {
    dailyInventoryLowerBounds[name].splice(period);
    dailyInventoryHigherBounds[name].splice(period);
  }
  if (business.recommend && business.recommend.length === 0) {
    // シミュレーション期間中に在庫が標準在庫指数を下回ることがなかった場合、ダミーのrecommend情報を入れておき、再計算されるのを防ぐ
    const recommendation: IArrival = {
      arrivalId: '',
      year: -1,
      month: -1,
      day: -1,
      amount: -1,
      leadTime: -1,
      isFixOrdered: undefined,
      fixOrderDate: undefined,
      crossDate: undefined,
      status: undefined,
      memoText: undefined,
      partialDeliveries: undefined,
      number: undefined,
      updateAt: undefined,
    };
    business.recommend.push(recommendation);
  }
  return { simData, dailyInventoryLowerBounds, dailyInventoryHigherBounds };
};

/**
 * ユーザーが入力したextraSimulationが存在 & isFirstSimulateがtrueの場合、extraSimulationを元にシミュレーションを行う
 * @param extraSimulation
 */
const isExtraSimulation = (extraSimulation?: IExtraSimulate): boolean => {
  if (!extraSimulation || extraSimulation.isFirstSimulate) {
    return false;
  }
  return true;
};

/**
 *
 * @param business
 * @param numOfBizDaysForSimulation
 * @param orderAmount
 * @param simulateDate
 * @param differenceFromStartDate
 * @param extraSimulate - シミュレーション用：extraSimulateを入れると、その情報を元にシミュレーションを行う(recommendの推奨は行わないようになる）
 * @param simulationStartDate - 指定すると、この日付より前のrecommendを抑制する
 */
const simulate = async (
  business: IBusiness,
  numOfBizDaysForSimulation: number,
  orderAmount: number | null = null,
  simulateDate: number,
  differenceFromStartDate?: number,
  extraSimulate?: IExtraSimulate,
  simulationStartDate?: Date
): Promise<
  | {
      simData: IDailySimResult[];
      dailyInventoryLowerBounds: { [key: string]: number[] };
      dailyInventoryHigherBounds: { [key: string]: number[] };
    }
  | undefined
> => {
  if (!business) return;

  const snapTime = simulateDate ? new Date(simulateDate) : new Date();
  if (differenceFromStartDate) {
    snapTime.setDate(snapTime.getDate() + differenceFromStartDate);
  }
  let now = moment();
  if (simulateDate) {
    now = moment(simulateDate);
  }
  if (orderAmount != null) {
    if (simulateDate) {
      now = moment(simulateDate);
    }
    // business.orderAmount = orderAmount;
  }

  const calendar = await getCalendar();

  return simulateInventory(business, calendar, snapTime, numOfBizDaysForSimulation, extraSimulate, false, simulationStartDate);
};

/**
 * シミュレーション範囲内の稼働日情報が登録されているかどうかを確認して、シミュレーション可能かどうかを判定する
 * シミュレーション範囲内の稼働日情報が登録されていない場合、仕入先内示数量0など、不正確な情報がシミュレーション結果として出てしまうため、処理を作成
 * @param calendar 登録されている稼働日カレンダー
 * @param startDate シミュレーション開始日(在庫の作成日)
 * @param period シミュレーション範囲(何日先までシミュレートするか)
 */
const confirmPossibleSimulate = async (calendar: ICalendar, startDate: Date, period?: SimulationPeriodType | number): Promise<boolean> => {
  // カレンダーに登録されている稼働日が0だったらシミュレーション不可とする
  if (!calendar.workDays.length) return false;
  // Date型の引数は、もらった時にstring | number | undefinedになるため、string | numberなら、Dateクラスに渡して再生成する必要がある
  startDate = startDate ? new Date(startDate) : new Date();
  // シミュレーション範囲の分岐
  switch (period) {
    case undefined:
      period = SIM_DAYS;
      break;
    case 'maxSimDays':
      period = MAX_SIM_DAYS;
      break;
    case 'minSimDays':
      period = SIM_DAYS;
      break;
  }
  // シミュレーション対象の情報を取得する
  const simDate = getBaseData(calendar, startDate, period);
  // シミュレーション対象の情報から重複を排除し、年月情報の配列に変換する
  const uniqueSimYearMonthArray: IYearMonth[] = Array.from(
    new Map(
      simDate.map((val) => [
        val.month,
        {
          year: val.year,
          month: val.month,
        },
      ])
    ).values()
  );
  // シミュレーション対象の年月情報の配列に含まれている稼働日情報を探す
  for (const yearMonth of uniqueSimYearMonthArray) {
    // 稼働日情報を探す
    const findWorkDays = calendar.workDays.find((val) => DateService.equalYearMonthInfo(val, yearMonth));
    // 判定結果を配列に貯める
    if (!findWorkDays || findWorkDays.days.length <= 5) {
      // 稼働日情報が見つからなかった場合は、登録されていないとする
      return false;
    }
  }
  return true;
};

export { getBaseData, calculationStdInventoryIndicesAmount }; // for testing
export { simulateInventory, simulate, confirmPossibleSimulate };
